import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { ErrorState, LoadingShell } from '@siliconx/shared/ui/state';
import { useEnvironmentalReportsQuery } from '../../hooks/useEnvironmentalReports';
import { MethodologyVerificationCard } from '../../components/environmental/MethodologyVerificationCard';
import { DisclosureMappingCard } from '../../components/environmental/DisclosureMappingCard';
import { AvailableReportsCard } from '../../components/environmental/AvailableReportsCard';
import { ScopeExclusionRegisterCard } from '../../components/environmental/ScopeExclusionRegisterCard';
import { ReportPackageComposerCard } from '../../components/environmental/ReportPackageComposerCard';
import { RegulatoryRegisterCard } from '../../components/environmental/RegulatoryRegisterCard';

/**
 * SIL-477 — Reports tab, live. Per `Environmental Reports.dc.html` amended
 * by the SIL-459 design-deviation register R5/R6: the mock's "Assurance"
 * card (Audit-ready badge, Third-party assured pill, ISAE reference,
 * "Filed"/"SE Verified" report badges) is replaced with a "Methodology &
 * verification" card carrying an upgradeable, server-provided status slot;
 * report statuses are Draft/Final with a real "data as of" timestamp, never
 * "Filed"/"assured"; the disclosure-mapping card frames GHG Protocol/CSRD
 * ESRS E1/CDP rows as export-ready extracts the customer files themselves.
 * Also hosts the full Scope 3 exclusion-justification register (moved from
 * Overview per SIL-464 — see ScopeExclusionRegisterCard's own doc comment).
 *
 * SIL-540/SIL-542: this tab has its own CUSTOMER_NAV entry ("ESG Reports"),
 * distinct from the shared "Carbon footprint" title the other environmental
 * tabs render via the parent layout — so it owns its own PageHeader here
 * (environmental.tsx steps aside on this route) to keep title and nav label
 * matched.
 *
 * SIL-577 (I2-D5) — adds `ReportPackageComposerCard`, a composition UI over
 * the exports already on this page (see that component's own doc comment).
 * No new export or methodology; the composer's manifest endpoint
 * (`POST /api/client/environmental/reports/package`) only names which
 * EXISTING files belong in one package.
 *
 * SIL-611 — adds `RegulatoryRegisterCard` (design option 1d): the static
 * four-row disposal/end-of-life regulation register, anchor id
 * `regulatory-register`. Copy is locked; see `regulationCopy.ts`.
 */
export const Route = createFileRoute('/environmental/reports')({
  component: ReportsTab,
});

function ReportsTab() {
  const reportsQ = useEnvironmentalReportsQuery();

  return (
    <div className="space-y-4">
      <PageHeader
        title="ESG Reports"
        pinTo="/environmental/reports"
        subtitle="Methodology, disclosure mapping, and filed reports for this fleet's carbon footprint."
      />

      {reportsQ.isLoading && <LoadingShell rows={4} />}
      {reportsQ.isError && (
        <ErrorState
          title="Could not load the Environmental reports"
          error={reportsQ.error}
          onRetry={() => void reportsQ.refetch()}
        />
      )}

      {reportsQ.data && (
        <div className="grid grid-cols-12 items-start gap-4">
          <div className="col-span-12 lg:col-span-5">
            <MethodologyVerificationCard verification={reportsQ.data.verification} />
          </div>
          <div className="col-span-12 lg:col-span-7">
            <DisclosureMappingCard />
          </div>
          <div className="col-span-12">
            <AvailableReportsCard reports={reportsQ.data.reports} />
          </div>
          <div className="col-span-12">
            <ReportPackageComposerCard />
          </div>
          <div className="col-span-12">
            <ScopeExclusionRegisterCard />
          </div>
          <div className="col-span-12">
            <RegulatoryRegisterCard />
          </div>
        </div>
      )}
    </div>
  );
}
